import React, { CSSProperties, useMemo, useState } from 'react';
import { get } from 'lodash';
import classNames from 'classnames/bind';
import Icon from '../Icon';
import { sortByNumberOrString } from './sorting';
import useWindowSize from '../../hooks/useWindowSize';
import { ITableColumn } from 'src/components/Table/types';
import { IContactTableRow } from 'src/components/ContactsTable/data';
import { OccupancyReportData } from 'src/pages/Reporting/ReportingFormTypes';
import { IServiceRequest } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import { ISuite } from 'src/components/SharedTypes';
import styles from './Table.module.scss';

interface ITableProps {
  columns: ITableColumn[];
  data:
    | IContactTableRow[]
    | OccupancyReportData[]
    | IServiceRequest[]
    | ISuite[];
  defaultSort?: {
    accessor: string;
    direction: 'ascending' | 'descending';
  };
  stickyHeader?: boolean;
  headerTop?: number | string;
  expandedGroups?: string[];
  applyMinWidth?: boolean;
  resizeMobileColumns?: boolean;
  editColumns?: boolean;
}

const Table = ({
  columns,
  data,
  defaultSort,
  stickyHeader = false,
  headerTop,
  expandedGroups = [],
  applyMinWidth = false,
  resizeMobileColumns = true,
  editColumns,
}: ITableProps) => {
  const [sort, setSort] = useState(
    defaultSort || {
      accessor: '',
      direction: '',
    }
  );

  const { isMobile } = useWindowSize();
  const { accessor, direction } = sort;

  const cx = classNames.bind(styles);

  const headerStyle = (style, left) => {
    return {
      ...style,
      marginTop: headerTop ?? style.top ?? undefined,
      left,
    };
  };

  const sortingAlgorithm =
    columns.find((c: ITableColumn) => c.accessor === accessor)
      ?.sortingAlgorithm || sortByNumberOrString;

  const sorted:
    | IContactTableRow[]
    | OccupancyReportData[]
    | IServiceRequest[]
    | ISuite[] = useMemo(() => {
    const sortedData:
      | IContactTableRow[]
      | OccupancyReportData[]
      | IServiceRequest[]
      | ISuite[] = data
      .slice()
      .sort(
        (
          a: IContactTableRow | OccupancyReportData | IServiceRequest | ISuite,
          b: IContactTableRow | OccupancyReportData | IServiceRequest | ISuite
        ) => {
          const valA: any = get(a, accessor);
          const valB: any = get(b, accessor);
          return sortingAlgorithm(valA, valB);
        }
      );

    return direction === 'ascending' ? sortedData.reverse() : sortedData;
  }, [data, accessor, sortingAlgorithm, direction]);

  const columnDefinitions: ITableColumn[] = useMemo(
    () =>
      [
        ...columns,
        editColumns
          ? {
              sortable: false,
              xsHide: false,
              textOverflow: false,
              label: (
                <span
                  style={{
                    backgroundColor: '#1c1c1c',
                    color: '#fff',
                    padding: '.6rem 1.5rem',
                    borderRadius: '2rem',
                    cursor: 'pointer',
                  }}
                >
                  <Icon glyph="pen" weight="solid" />
                </span>
              ),
              width: '7rem',
            }
          : null,
      ].filter(Boolean),
    [columns, editColumns]
  );

  const tableColumnTemplate = useMemo(() => {
    return columnDefinitions.reduce(
      (template: string, column: ITableColumn) => {
        if (isMobile && column.xsHide) {
          return template;
        }
        if (isMobile && resizeMobileColumns) {
          return template + ` ${'1fr'}`;
        }
        if (column.minWidth && column.width) {
          return template + ` minmax(${column.minWidth}, ${column.width})`;
        }
        if (column.minWidth && !column.width) {
          return template + ` minmax(${column.minWidth}, 1fr)`;
        }
        if (!column.minWidth && column.width) {
          return template + ` ${column.width}`;
        }
        return template + ' 1fr';
      },
      ''
    );
  }, [columnDefinitions, isMobile]);

  const minWidth: any[] = applyMinWidth
    ? columnDefinitions.reduce((filtered: any[], column: ITableColumn) => {
        (column.minWidth || column.width) &&
          filtered.push(column.minWidth ?? column.width ?? '0rem');
        return filtered;
      }, [])
    : [];

  return (
    <table
      className={styles.table}
      style={{
        minWidth:
          !isMobile || (isMobile && !resizeMobileColumns)
            ? `calc(${minWidth.join(' + ')})`
            : null,
        gridTemplateColumns: tableColumnTemplate,
      }}
    >
      <thead className={styles.table__header}>
        <tr className={styles.table__row}>
          {columnDefinitions.map((c: ITableColumn, i: number) => (
            <th
              key={`table-header-${i}`}
              className={cx(styles.table__header, {
                center: c.center,
                'hide-xs': c.xsHide,
                stickyHeader: stickyHeader,
                stickyRow: c.sticky,
              })}
              style={headerStyle(c.style ?? {}, c.left)}
            >
              <div></div>
              {c.sortable ? (
                <button
                  className={styles.sortingHeader}
                  onClick={() =>
                    setSort({
                      accessor: c.accessor,
                      direction:
                        direction === 'ascending' ? 'descending' : 'ascending',
                    })
                  }
                >
                  {c.label}
                  {c.accessor === accessor && (
                    <Icon
                      glyph={
                        direction === 'ascending' ? 'caret-up' : 'caret-down'
                      }
                      weight="solid"
                    />
                  )}
                </button>
              ) : (
                c.label
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sorted.map(
          (
            d:
              | IContactTableRow
              | OccupancyReportData
              | IServiceRequest
              | ISuite,
            i: number
          ) => {
            if (!d.isGroup) {
              return (
                <TableRow data={d} columns={columnDefinitions} index={i} />
              );
            } else {
              return (
                <TableGroupRow
                  data={d}
                  columns={columnDefinitions}
                  index={i}
                  isExpanded={expandedGroups.includes(d.id)}
                  isMobile={isMobile}
                />
              );
            }
          }
        )}
      </tbody>
    </table>
  );
};

interface ITableRowProps {
  data: any;
  columns: ITableColumn[];
  index: number | string;
  style?: CSSProperties;
  emphasis?: boolean;
}

const TableRow = ({
  data,
  columns,
  index,
  style,
  emphasis = false,
}: ITableRowProps) => {
  const cx = classNames.bind(styles);
  return (
    <tr
      key={`table-row-container${index}`}
      className={cx('table__row', { 'table__row--emphasis': emphasis })}
      style={style}
    >
      {columns.map((c: ITableColumn, x: number) => (
        <td
          key={`table-td=${x}-${data.id}-${index}`}
          className={cx('table__data', c.label === 'Action' && 'text-center', {
            center: c.center,
            'hide-xs': c.xsHide,
            'table__data--text-overflow': c.textOverflow,
            stickyRow: c.sticky,
            emphasis: emphasis,
          })}
          style={{ left: c.left, gridColumnStart: x + 1 }}
        >
          {c.cell ? c.cell(data) : get(data, c.accessor)}
        </td>
      ))}
    </tr>
  );
};

interface ITableGroupRowProps {
  data: any;
  columns: ITableColumn[];
  index: number;
  style?: React.CSSProperties;
  isExpanded: boolean;
  isMobile: boolean;
}

const TableGroupRow = ({
  data,
  columns,
  index,
  style,
  isExpanded,
  isMobile,
}: ITableGroupRowProps) => {
  const cx = classNames.bind(styles);
  return (
    <React.Fragment key={`group-header-container-${index}`}>
      <tr
        key={'group-header-' + data.id}
        className={cx('table__row', {
          'table__row--emphasis': isExpanded,
        })}
        style={style}
      >
        {columns.map((c: ITableColumn, x: number) =>
          c.groupedHeaderCell ? (
            <td
              key={`group-td=${x}-${data.id}-${index}`}
              colSpan={
                (isMobile
                  ? c.xsGroupedHeaderCellSpan
                  : c.groupedHeaderCellSpan) ?? 1
              }
              style={{
                left: c.left,
                gridColumnStart: x + 1,
                overflow: 'visible',
              }}
              className={cx(
                'table__data',
                c.label === 'Action' && 'text-center',
                {
                  center: c.center,
                  xsHide: c.xsHide,
                  emphasis: isExpanded,
                  stickyRow: c.sticky,
                }
              )}
            >
              {c.groupedHeaderCell &&
                c.groupedHeaderCell({ ...data, isExpanded })}
            </td>
          ) : (
            <td
              key={`group-td=${x}-${data.id}-${index}`}
              style={{ left: c.left, gridColumnStart: x + 1 }}
              className={cx(
                'table__data',
                c.label === 'Action' && 'text-center',
                {
                  center: c.center,
                  xsHide: c.xsHide,
                  emphasis: isExpanded,
                  stickyRow: c.sticky,
                }
              )}
            />
          )
        )}
      </tr>
      {isExpanded &&
        data.children.map((d: any, i: any) => (
          <TableRow
            data={{ ...d, isChild: true }}
            columns={columns}
            index={`${i}-group-${index}`}
            emphasis
          />
        ))}
    </React.Fragment>
  );
};

export default Table;
